// The key-hint line (desktop #hud, the phone's #mob-keys, the VR HUD's keys
// row): which controls to show, per platform and player state, packed into
// pages that each fit ONE line of the row's current footprint. Everything fits
// → one page, shown statically; otherwise the pages cycle every KH.cycleMs
// with a KH.fadeMs cross-fade (prefers-reduced-motion: no fade, KH.reducedMs).
// The content is defined ONCE per platform here (keyHintGroups) and every
// binding is a real one: FlatControls / main.js tick (desktop), MobileControls
// (phone), VRControls + vrui (VR) — APP_PLAYER "Key hints".
// Pure apart from mountHints (DOM; desktop + phone); the gate
// (scripts/check-i18n.mjs) imports it with the dictionaries' own t().

export const KH = { cycleMs: 4000, fadeMs: 400, reducedMs: 8000 };

// a line's budget in width units (khWidth): the row's footprint before the
// pages — desktop = the old walking line (EN, mic on: 64 chars + 7 keycaps),
// phone = the strip between the stick and the zoom slider of a 640 px wide
// landscape phone, VR = the status panel's text width (1224 px at 28 px)
export const KH_BUDGET = { desktop: 75, phone: 50, vr: 84 };
export const KH_SEP = { desktop: ' ', phone: ' · ', vr: ' · ' };

// visible characters + 1.5 per <kbd> (its padding + border at 11 px) + 1
// per pictograph (an emoji is about two characters wide)
export function khWidth(s) {
  const kbd = (String(s).match(/<kbd>/g) || []).length;
  const vis = String(s).replace(/<[^>]*>/g, '');
  return [...vis].length + 1.5 * kbd + (vis.match(/[\u{1F000}-\u{1FAFF}]/gu) || []).length;
}

// a group too long for one line → the fewest contiguous chunks that fit,
// balanced (the widest chunk as narrow as it can be)
function split(items, budget, sep, width) {
  const n = items.length, w = (a, b) => width(items.slice(a, b).join(sep));
  for (let k = 2; k <= n; k++) {
    let best = null;
    const go = (a, left, cuts, worst) => {
      if (left === 1) {
        const m = Math.max(worst, w(a, n));
        if (!best || m < best.m) best = { m, cuts: [...cuts, n] };
        return;
      }
      for (let b = a + 1; b <= n - left + 1; b++) go(b, left - 1, [...cuts, b], Math.max(worst, w(a, b)));
    };
    go(0, k, [], 0);
    if (best.m <= budget || k === n) {
      let a = 0;
      return best.cuts.map((b) => { const c = items.slice(a, b); a = b; return c; });
    }
  }
  return [items];
}

// groups of items (each item one binding) → pages: all in one page when it
// fits; else one page per group (a group too long split, split), adjacent
// WHOLE groups merged while the merge fits (a split group's pieces stay apart)
export function paginate(groups, budget, sep = ' ', width = khWidth) {
  const gs = groups.map((g) => g.filter(Boolean)).filter((g) => g.length);
  const all = gs.flat();
  if (!all.length) return [];
  if (width(all.join(sep)) <= budget) return [all.join(sep)];
  const out = [];
  for (const g of gs) {
    if (width(g.join(sep)) > budget) { for (const c of split(g, budget, sep, width)) out.push({ c, whole: false }); continue; }
    const last = out[out.length - 1];
    if (last && last.whole && width([...last.c, ...g].join(sep)) <= budget) last.c.push(...g);
    else out.push({ c: [...g], whole: true });
  }
  return out.map((p) => p.c.join(sep));
}

// the state the hints depend on (main.js fills it at the HUD cadence):
// { bike, follow, glide, riding, combat, mono, voice, muted,   (every platform)
//   compass, gyro }                                            (phone)
// → [[item…]…] groups: move · view · act · combat
export function keyHintGroups(platform, s, t) {
  if (platform === 'desktop') {
    if (s.bike) return [[t(s.follow ? 'main.hud.keysBikeAuto' : 'main.hud.keysBikeMan')]];
    if (s.glide) return [[t('main.hud.keysGlide')]];
    if (s.riding) return [];
    return [
      [t('main.kh.d.move'), t('main.kh.d.look'), t('main.kh.d.sprint'), t('main.kh.d.jump'), t('main.kh.d.climb')],
      [t('main.kh.d.zoom'), t('main.kh.d.map'), !s.combat && s.mono ? t('main.hud.keyInspect') : null,
        !s.combat && !s.mono ? t('main.hud.keyFire') : null],
      [t('main.kh.d.bike'), s.voice ? t(s.muted ? 'main.hud.keyMicMuted' : 'main.hud.keyMic') : null, t('main.kh.d.help')],
      s.combat ? [t('main.hud.keysCombat'), !s.mono ? t('main.hud.keyFire') : null] : [],
    ];
  }
  if (platform === 'phone') {
    if (s.glide) return [[t('main.kh.p.glide')]];
    if (s.riding) return [];
    return [
      [t(s.compass ? 'main.kh.p.walk' : 'main.kh.p.walkTurn'),
        t(s.compass ? 'main.kh.p.lookPoint' : s.gyro ? 'main.kh.p.lookBoth' : 'main.kh.p.lookDrag'),
        t('main.kh.p.jump')],
      [t('main.kh.p.zoom'), t(s.gyro ? 'main.kh.p.mapTilt' : 'main.kh.p.mapBtn')],
      [s.combat ? t('main.kh.p.fire') : t('main.kh.p.inspect'), t('main.kh.p.gps'), s.gyro ? t('main.kh.p.lock') : null],
    ];
  }
  // vr (no combat binding in VR: APP_PLAYER "VR controls")
  if (s.bike) return [[t(s.follow ? 'main.vr.keysBikeAuto' : 'main.vr.keysBikeMan')]];
  if (s.glide) return [[t('main.vr.keysGlide')]];
  return [
    [t('main.kh.v.move'), t('main.kh.v.turn'), t('main.kh.v.jump'), t('main.kh.v.climb')],
    [t('main.kh.v.zoom'), t('main.kh.v.map'), t('main.kh.v.point'), t('main.kh.v.menu'), t('main.kh.v.bike'),
      s.voice ? t(s.muted ? 'main.vr.micMuted' : 'main.vr.mic') : null],
  ];
}

export function keyHintPages(platform, s, t) {
  return paginate(keyHintGroups(platform, s, t), KH_BUDGET[platform], KH_SEP[platform]);
}

// the cycle over time (pure): set(pages, now) restarts at page 0 on a CHANGED
// set (returns true), at(now) → { i, prev, k } — the page shown, the one it
// replaced and the fade progress 0..1 since the switch (1 = settled; a single
// page never switches)
export class HintCycle {
  constructor(o = {}) {
    this.cycleMs = o.cycleMs || KH.cycleMs;
    this.fadeMs = o.fadeMs ?? KH.fadeMs;
    this.pages = []; this.sig = null; this.t0 = 0;
  }
  set(pages, now) {
    const sig = pages.join('\u0001');
    if (sig === this.sig) return false;
    this.sig = sig; this.pages = pages; this.t0 = now;
    return true;
  }
  at(now) {
    const n = this.pages.length;
    if (n < 2) return { i: 0, prev: 0, k: 1 };
    const el = Math.max(0, now - this.t0), step = Math.floor(el / this.cycleMs);
    const i = step % n;
    const since = el - step * this.cycleMs;
    if (step === 0 || !this.fadeMs) return { i, prev: i, k: 1 };
    return { i, prev: (i + n - 1) % n, k: Math.min(1, since / this.fadeMs) };
  }
}

const _reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
export const khReduced = _reduced;

// desktop + phone: every page of the set stacked in ONE grid cell (the row is
// as wide as its widest page, so a swap moves nothing), the shown one .on;
// CSS fades opacity (.kh-page, index.html / mobile.js). The DOM is written
// only when the set changes; the cycle timer toggles two classes.
export function mountHints(el) {
  let sig = null, i = 0, timer = 0, nodes = [];
  const stop = () => { if (timer) clearTimeout(timer); timer = 0; };
  const step = () => {
    nodes[i].classList.remove('on');
    i = (i + 1) % nodes.length;
    nodes[i].classList.add('on');
    timer = setTimeout(step, _reduced() ? KH.reducedMs : KH.cycleMs);
  };
  return {
    // pages: html strings (desktop) or plain text (phone, escaped by the caller)
    set(pages) {
      const s = pages.join('\u0001');
      if (s === sig) return;
      sig = s; stop(); i = 0;
      el.innerHTML = pages.map((p, k) => `<span class="kh-page${k ? '' : ' on'}">${p}</span>`).join('');
      nodes = [...el.children];
      el.style.display = pages.length ? '' : 'none';
      if (pages.length > 1) timer = setTimeout(step, _reduced() ? KH.reducedMs : KH.cycleMs);
    },
    stop,
  };
}
